<template>
  <div class="dashboard-container">
    <div class="section-title">快捷入口</div>
    <div class="quick-entries">
      <div class="entry-item" v-for="item in quickEntries" :key="item.path" @click="goPage(item)">
        <div class="entry-icon" :style="{ backgroundColor: item.color }">
          <i :class="item.icon"></i>
        </div>
        <div class="entry-text">{{ item.text }}</div>
      </div>
    </div>

    <div class="stats-cards">
      <div class="stat-card" v-loading="inventoryLoading">
        <div class="stat-title">总库存量</div>
        <div class="stat-value">{{ formatWeight(totalStockWeight) }} 吨</div>
        <div class="stat-desc">煤棚数量 {{ shedCount }} 个</div>
        <div class="progress-bar">
          <div class="progress" :style="{ width: stockRate + '%', backgroundColor: '#1890ff' }"></div>
        </div>
      </div>

      <div class="stat-card" v-loading="planLoading">
        <div class="stat-title">今日计划完成</div>
        <div class="stat-value success">{{ completionRate }}%</div>
        <div class="stat-desc">已完成 {{ formatWeight(doneAmount) }} / 计划 {{ formatWeight(planAmount) }} 吨</div>
        <div class="progress-bar">
          <div class="progress" :style="{ width: completionRate + '%', backgroundColor: '#52c41a' }"></div>
        </div>
      </div>

      <div class="stat-card" v-loading="planLoading">
        <div class="stat-title">今日作业状态</div>
        <div class="stat-value">{{ operationTypeCount }} 类</div>
        <div class="stat-desc">{{ operationTypeDesc }}</div>
        <div class="status-indicator">
          <span class="dot" :style="{ backgroundColor: planStatusColor }"></span>
          <span>{{ planStatusText }}</span>
        </div>
      </div>
    </div>

    <div class="section-title">生产进度</div>
    <div class="production-charts">
      <div class="chart-card" v-loading="inventoryLoading">
        <div class="chart-title">库存实时监控</div>
        <div class="stock-info">
          <div class="stock-value">{{ formatWeight(totalStockWeight) }} 吨</div>
          <div class="stock-desc">按当前库存快照汇总</div>
        </div>
        <div ref="gaugeChart" class="gauge-chart"></div>
      </div>

      <div class="chart-card" v-loading="trendLoading">
        <div class="chart-title">库存趋势</div>
        <div ref="trendChart" class="trend-chart"></div>
      </div>

      <div class="chart-card" v-loading="planLoading">
        <div class="chart-title">今日计划量及完成情况</div>
        <div class="progress-container">
          <div class="progress-header">
            <span>今日计划量 {{ formatWeight(planAmount) }} 吨</span>
            <span>已完成 {{ formatWeight(doneAmount) }} 吨</span>
          </div>
          <el-progress :percentage="completionRate" :stroke-width="6" color="#42b983" />
        </div>
        <div ref="barChart" class="bar-chart"></div>
      </div>

      <div class="chart-card" v-loading="planLoading">
        <div class="chart-title">作业类型完成率</div>
        <div ref="lineChart" class="line-chart"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import 'echarts/theme/macarons'
import { mapGetters } from 'vuex'
import { isURL } from '@/util/validate'
import {
  getDailyPlanStatus,
  getInventoryTrendAnalysis,
  getRealtimeInventoryOverview
} from '../api'

export default {
  data() {
    return {
      entryColors: [
        'rgba(24, 144, 255, 0.15)',
        'rgba(82, 196, 26, 0.15)',
        'rgba(153, 102, 255, 0.15)',
        'rgba(255, 153, 0, 0.15)',
        'rgba(245, 34, 45, 0.15)',
        'rgba(19, 194, 194, 0.15)',
        'rgba(250, 173, 20, 0.15)',
        'rgba(47, 84, 235, 0.15)'
      ],
      inventoryLoading: false,
      trendLoading: false,
      planLoading: false,
      inventoryList: [],
      inventoryTrend: { dates: [], series: [] },
      planSummary: {},
      planMetrics: [],
      operationTypeCount: 0,
      gaugeChart: null,
      trendChart: null,
      barChart: null,
      lineChart: null
    }
  },
  computed: {
    ...mapGetters(['menu', 'menuAll', 'website']),
    menuProps() {
      const props = this.website && this.website.menu && this.website.menu.props ? this.website.menu.props : {}
      return {
        label: props.label || 'name',
        path: props.path || 'path',
        icon: props.icon || 'icon',
        children: props.children || 'children',
        meta: props.meta || 'meta'
      }
    },
    quickEntries() {
      const source = Array.isArray(this.menu) && this.menu.length ? this.menu : this.menuAll
      const leafMenus = this.flattenLeafMenus(Array.isArray(source) ? source : [])
      return leafMenus.slice(0, 8).map((item, index) => {
        const props = this.menuProps
        return {
          text: item[props.label],
          icon: item[props.icon] || 'el-icon-menu',
          color: this.entryColors[index % this.entryColors.length],
          path: item[props.path],
          query: item.query,
          meta: item[props.meta],
          raw: item
        }
      })
    },
    totalStockWeight() {
      return this.inventoryList.reduce((sum, item) => sum + this.toNumber(item.totalInventory), 0)
    },
    shedCount() {
      return this.inventoryList.length
    },
    stockRate() {
      return this.totalStockWeight > 0 ? 85 : 0
    },
    planAmount() {
      if (this.planSummary && Object.prototype.hasOwnProperty.call(this.planSummary, 'planWeight')) {
        return this.toNumber(this.planSummary.planWeight)
      }
      return this.planMetrics.reduce((sum, item) => sum + this.toNumber(item.planVolume), 0)
    },
    doneAmount() {
      if (this.planSummary && Object.prototype.hasOwnProperty.call(this.planSummary, 'executeWeight')) {
        return this.toNumber(this.planSummary.executeWeight)
      }
      return this.planMetrics.reduce((sum, item) => sum + this.toNumber(item.actualVolume), 0)
    },
    completionRate() {
      if (this.planSummary && Object.prototype.hasOwnProperty.call(this.planSummary, 'completionRate')) {
        return Math.min(100, Math.round(this.toNumber(this.planSummary.completionRate)))
      }
      if (!this.planAmount) return 0
      return Math.min(100, Math.round((this.doneAmount / this.planAmount) * 100))
    },
    plannedPlanMetrics() {
      return this.planMetrics.filter((item) => this.toNumber(item.planVolume) > 0)
    },
    operationTypeDesc() {
      if (!this.operationTypeCount) return '暂无计划'
      return this.plannedPlanMetrics.map((item) => item.title).join('、')
    },
    planStatusText() {
      if (!this.planAmount) return '暂无计划'
      return this.completionRate >= 80 ? '运行正常' : '持续跟踪'
    },
    planStatusColor() {
      if (!this.planAmount) return '#d9d9d9'
      return this.completionRate >= 80 ? '#52c41a' : '#faad14'
    }
  },
  mounted() {
    this.initCharts()
    this.loadInventory()
    this.loadInventoryTrend()
    this.loadDailyPlan()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.disposeCharts()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    goPage(item) {
      if (!item || !item.path) return
      const targetPath = this.$router.$avueRouter
        ? this.$router.$avueRouter.getPath({ name: item.text, src: item.path }, item.meta)
        : item.path
      this.$router.push({
        path: targetPath,
        query: item.query
      })
    },
    flattenLeafMenus(list) {
      const props = this.menuProps
      const result = []
      const walk = (menus) => {
        menus.forEach((item) => {
          if (!item) return
          const children = Array.isArray(item[props.children]) ? item[props.children] : []
          if (children.length) {
            walk(children)
            return
          }
          const path = item[props.path]
          const label = item[props.label]
          if (!path || !label || isURL(path)) return
          result.push(item)
        })
      }
      walk(list)
      return result
    },
    toNumber(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    },
    formatWeight(value) {
      return this.toNumber(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    loadInventory() {
      this.inventoryLoading = true
      getRealtimeInventoryOverview()
        .then((data) => {
          this.inventoryList = Array.isArray(data) ? data : []
          this.renderGauge()
        })
        .catch(() => {
          this.inventoryList = []
          this.renderGauge()
        })
        .finally(() => {
          this.inventoryLoading = false
        })
    },
    loadInventoryTrend() {
      this.trendLoading = true
      getInventoryTrendAnalysis()
        .then((data) => {
          this.inventoryTrend = data || { dates: [], series: [] }
          this.renderTrend()
        })
        .catch(() => {
          this.inventoryTrend = { dates: [], series: [] }
          this.renderTrend()
        })
        .finally(() => {
          this.trendLoading = false
        })
    },
    loadDailyPlan() {
      this.planLoading = true
      getDailyPlanStatus()
        .then((data) => {
          const lines = data && Array.isArray(data.lines) ? data.lines : []
          this.planSummary = data && data.summary ? data.summary : {}
          this.planMetrics = lines
          this.operationTypeCount = Object.prototype.hasOwnProperty.call(data || {}, 'operationTypeCount')
            ? this.toNumber(data.operationTypeCount)
            : lines.filter((item) => this.toNumber(item.planVolume) > 0).length
          this.renderPlanCharts()
        })
        .catch(() => {
          this.planSummary = {}
          this.planMetrics = []
          this.operationTypeCount = 0
          this.renderPlanCharts()
        })
        .finally(() => {
          this.planLoading = false
        })
    },
    initCharts() {
      this.gaugeChart = echarts.init(this.$refs.gaugeChart, 'macarons')
      this.trendChart = echarts.init(this.$refs.trendChart, 'macarons')
      this.barChart = echarts.init(this.$refs.barChart, 'macarons')
      this.lineChart = echarts.init(this.$refs.lineChart, 'macarons')
      this.renderGauge()
      this.renderTrend()
      this.renderPlanCharts()
    },
    renderGauge() {
      if (!this.gaugeChart) return
      this.gaugeChart.setOption({
        series: [{
          type: 'gauge',
          startAngle: 90,
          endAngle: -270,
          pointer: { show: false },
          progress: { show: true, roundCap: true, itemStyle: { color: '#1890ff' } },
          axisLine: { lineStyle: { width: 15 } },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          detail: { valueAnimation: true, fontSize: 20, formatter: '{value}%' },
          data: [{ value: this.stockRate }]
        }]
      })
    },
    renderTrend() {
      if (!this.trendChart) return
      const series = (this.inventoryTrend.series || []).map((item) => ({
        name: item.name,
        type: 'line',
        smooth: true,
        data: item.data || []
      }))
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0 },
        grid: { left: '3%', right: '4%', bottom: '3%', top: 40, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: this.inventoryTrend.dates || [] },
        yAxis: { type: 'value' },
        series
      }, true)
    },
    renderPlanCharts() {
      if (!this.barChart || !this.lineChart) return
      const metrics = this.planMetrics
      const names = metrics.map((item) => item.title)
      const plan = metrics.map((item) => this.toNumber(item.planVolume))
      const actual = metrics.map((item) => this.toNumber(item.actualVolume))
      const rates = metrics.map((item) => this.toNumber(item.completionRate))
      this.barChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { data: ['计划量', '完成量'] },
        grid: { left: '3%', right: '4%', bottom: '3%', top: 40, containLabel: true },
        xAxis: { type: 'category', data: names },
        yAxis: { type: 'value' },
        series: [
          { name: '计划量', type: 'bar', data: plan, itemStyle: { color: '#1890ff' } },
          { name: '完成量', type: 'bar', data: actual, itemStyle: { color: '#52c41a' } }
        ]
      }, true)
      this.lineChart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', top: 30, containLabel: true },
        xAxis: { type: 'category', data: names },
        yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
        series: [{ name: '完成率', type: 'line', smooth: true, data: rates, itemStyle: { color: '#faad14' } }]
      }, true)
    },
    handleResize() {
      this.gaugeChart && this.gaugeChart.resize()
      this.trendChart && this.trendChart.resize()
      this.barChart && this.barChart.resize()
      this.lineChart && this.lineChart.resize()
    },
    disposeCharts() {
      this.gaugeChart && this.gaugeChart.dispose()
      this.trendChart && this.trendChart.dispose()
      this.barChart && this.barChart.dispose()
      this.lineChart && this.lineChart.dispose()
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
}
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px;
}
.quick-entries {
  display: grid;
  grid-template-columns: repeat(8, minmax(90px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.entry-item {
  background: #fff;
  border-radius: 8px;
  padding: 14px 8px;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.entry-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  margin: 0 auto 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.entry-text {
  font-size: 14px;
  color: #333;
}
.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}
.stat-card,
.chart-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.stat-title,
.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1890ff;
}
.stat-value.success {
  color: #52c41a;
}
.stat-desc,
.stock-desc {
  font-size: 13px;
  color: #666;
  margin-top: 8px;
}
.progress-bar {
  height: 6px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 14px;
}
.progress {
  height: 100%;
  transition: width 0.3s;
}
.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  color: #666;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.production-charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 1fr));
  gap: 20px;
}
.stock-info {
  margin-bottom: 8px;
}
.stock-value {
  font-size: 24px;
  color: #1890ff;
  font-weight: 700;
}
.gauge-chart,
.trend-chart,
.bar-chart,
.line-chart {
  height: 260px;
}
.progress-container {
  margin-bottom: 12px;
}
.progress-header {
  display: flex;
  justify-content: space-between;
  color: #666;
  font-size: 13px;
  margin-bottom: 8px;
}
@media (max-width: 1200px) {
  .quick-entries {
    grid-template-columns: repeat(4, minmax(90px, 1fr));
  }
}
@media (max-width: 768px) {
  .quick-entries,
  .stats-cards,
  .production-charts {
    grid-template-columns: 1fr;
  }
}
</style>
